<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import brownInsert from './images/LP-Compatability-w-Blinds-01.jpg';

	import tapeMeasure from './images/LP-Compatability-w-Blinds-02.svg';

	import blindsBehind1 from './images/LP-Compatability-w-Blinds-03a.svg';
	import blindsBehind2 from './images/LP-Compatability-w-Blinds-03b.svg';
	import blindsBehind3 from './images/LP-Compatability-w-Blinds-03c.svg';
	import bathroomBlinds from './images/LP-Compatability-w-Blinds-04.jpg';
	import blindsInFront from './images/LP-Compatability-w-Blinds-05.jpg';
	import shuttersDiagram from './images/LP-Compatability-w-Blinds-08.svg';

	import blindsSlide1 from './images/LP-Compatability-w-Blinds-06a.svg';
	import blindsSlide2 from './images/LP-Compatability-w-Blinds-06b.svg';
	import blindsSlide3 from './images/LP-Compatability-w-Blinds-06c.svg';
	import blindsSlide4 from './images/LP-Compatability-w-Blinds-06d.svg';
	import blindsSlide5 from './images/LP-Compatability-w-Blinds-06e.svg';
</script>

<Section>
	<p class="max-md:text-left">
		When you have an inside-mounted blind or shade in your window frame, there
		are a couple options to achieve a tight fit. Whether you plan on installing
		between your blinds or over your blinds, make sure to follow our spacing
		guidelines for the best fit when installing your insert. Contact your Fit
		Specialist or <a href="/how-to/get-indow-inserts">Start An Estimate</a>
		if you have any questions about installing Indow inserts with blinds or shutters.
	</p>

	<Image class="no-bleed shadow-xl" src={brownInsert} />

	<p class="max-md:text-left">
		For the silicone tubing to achieve a 99% airtight seal, Indow Inserts are
		made to the exact specific dimensions of your frames. The inserts must be
		installed perpendicular to the window frames, they cannot be installed at an
		angle to clear the blinds.
	</p>

	<p class="max-md:text-left">
		For those seeking noise reduction, we want you to trap as much air between
		the insert and window as possible. Trapping air allows you to trap sound. An
		inch of airspace is recommended, but more airspace is better; trapping three
		inches of airspace can reduce noise by nearly two additional decibels.
	</p>
</Section>

<Section>
	<h2
		class="
    w-full
    max-md:text-left
  "
	>
		<span class="text-indow-grey-dark">Option 1:</span><br />
		<span>Installing Behind Blinds</span>
	</h2>

	<p
		class="
    text-indow-grey-dark
    max-md:text-left
  "
	>
		This diagram shows there is more than enough room at the top of the frame
		step between the window and the hardware for the blinds. The green area
		depicts the minimum amount of space needed for the insert.
	</p>

	<p class="my-10">
		<Image class="shadow-xl" src={tapeMeasure} />
	</p>

	<p class="max-md:text-left">
		Installing your Indow insert between the blinds and the glass is a great
		option for people who are primarily seeking energy efficiency and comfort.
	</p>

	<p
		class="
    font-medium
    max-md:text-left
  "
	>
		To install your blinds alongside your Indow insert, you have two options:
	</p>
	<ul
		class="
    w-fit
    [&_li_p]:text-left
  "
	>
		<li>
			<p class="w-full text-left">
				Remove your blinds, position the Indow insert closer to the glass, and
				then rehang your blinds.
			</p>
		</li>
		<li>
			<p class="w-full text-left">
				Raise your blinds and follow the steps outlined below.
			</p>
		</li>
	</ul>

	<div class="mb-8 flex flex-col-reverse">
		<h3 class="text-indow-grey-dark">INSERT INSIDE WINDOW FROM BEHIND BLIND</h3>
		<ul
			class="
     grid list-none grid-cols-3 gap-0
     sm:gap-4
   "
		>
			<li>
				<Image src={blindsBehind1} />
			</li>
			<li>
				<Image src={blindsBehind2} />
			</li>
			<li>
				<Image src={blindsBehind3} />
			</li>
		</ul>
	</div>

	<ol
		class="
    mb-12 w-fit list-decimal
    [&_li_p]:w-full [&_li_p]:text-left
  "
	>
		<li>
			<p>
				Pull blinds all the way up so they're out of the way. Make sure there's
				1 ¼" of depth between the blinds and glass.
			</p>
		</li>
		<li>
			<p>
				Align the insert so it's in the center of the window, slide the insert
				up behind the blinds.
			</p>
		</li>
		<li>
			<p>Push the insert upwards to compress the top tubing.</p>
		</li>
		<li>
			<p>
				Bend the insert and tuck the bottom of the insert into the frame. Apply
				gentle and equal pressure to the left and right sides of the tubing,
				until the insert is fully seated in the window frame.
			</p>
		</li>
		<li>
			<p>
				Test blinds to make sure the insert is positioned correctly and there
				are no obstructions.
			</p>
		</li>
	</ol>
	<Image src={bathroomBlinds} class="no-bleed shadow-xl" />
</Section>

<Section backgroundColor="blue" imageColumn={blindsInFront}>
	<h2
		class="
    w-full text-left
    md:text-center
  "
	>
		<span class="text-indow-grey-dark">Option 2:</span><br />
		<span>Installing In Front Of Blinds</span>
	</h2>
	<p
		class="
    text-left
    md:max-w-md
  "
	>
		This option lets you keep your blinds between the insert and the glass,
		taking full advantage of your window's depth. You'll need at least 5/8" of
		space in front of your blinds.
	</p>
	<p
		class="
    text-left
    md:max-w-md
  "
	>
		This is ideal for motorized blinds or blinds that typically stay in one
		position. The convenient metal D-ring on the bottom left corner allows for
		quick and easy removal of the insert whenever you need to adjust your
		blinds.
	</p>
</Section>

<Section>
	<h2
		class="
    w-full text-indow-grey-dark
    max-md:text-left
  "
	>
		Option 3:<br />
		<span class="text-balance text-indow-blue">Move Or Remove Your Blinds</span>
	</h2>
	<p class="max-md:text-left">
		Many customers, especially those seeking noise reduction, find it's best to
		remove the blinds to get optimal results. You can then remount your blinds
		just outside and above the window frames. To conceal the blinds, you might
		add a wooden valance. Alternatively, you could switch to sheers or curtains.
	</p>

	<div class="flex flex-col-reverse">
		<style>
			@keyframes fadeOut {
				0% {
					opacity: 1;
				}
				6% {
					opacity: 0;
				}
				80% {
					opacity: 0;
				}
				90% {
					opacity: 1;
				}
				100% {
					opacity: 1;
				}
			}
		</style>
		<h3 class="text-indow-grey-dark">
			MOVING A BLIND FROM AN INSIDE MOUNT TO AN OUTSIDE MOUNT
		</h3>
		<ul class="relative grid list-none grid-cols-1 grid-rows-1 gap-4">
			<li
				class="
      inset-0 col-span-full row-span-full animate-[fadeOut_10s_linear_infinite]
      bg-indow-white
    "
				style="animation-delay: 8s;"
			>
				<Image src={blindsSlide5} class="w-120 max-w-full" />
			</li>
			<li
				class="
      inset-0 col-span-full row-span-full animate-[fadeOut_10s_linear_infinite]
      bg-indow-white
    "
				style="animation-delay: 6s;"
			>
				<Image src={blindsSlide4} class="w-120 max-w-full" />
			</li>
			<li
				class="
      inset-0 col-span-full row-span-full animate-[fadeOut_10s_linear_infinite]
      bg-indow-white
    "
				style="animation-delay: 4s;"
			>
				<Image src={blindsSlide3} class="w-120 max-w-full" />
			</li>
			<li
				class="
      inset-0 col-span-full row-span-full max-w-full
      animate-[fadeOut_10s_linear_infinite] bg-indow-white
    "
				style="animation-delay: 2s;"
			>
				<Image src={blindsSlide2} class="w-120 max-w-full" />
			</li>
			<li
				class="
      inset-0 col-span-full row-span-full animate-[fadeOut_10s_linear_infinite]
      bg-indow-white
    "
				style="animation-delay: 0s;"
			>
				<Image src={blindsSlide1} class="w-120 max-w-full" />
			</li>
		</ul>
	</div>
</Section>
<Section backgroundColor="grey-dark">
	<h2 class="max-md:text-left">When Plantation Shutters Are Installed</h2>
	<div
		class="
    grid grid-cols-1 gap-4
    md:grid-cols-2
  "
	>
		<div>
			<p class="text-left">
				If your shutters are inside your window frame and have stops (those
				little pieces that prevent the shutters from swinging too far) on more
				than one side, it might be tricky to install the insert because the
				stops get in the way. This requires a workaround.
			</p>
			<p class="text-left">
				The inserts need a clear path to slide into place. Since they can't fit
				through a space narrower than themselves, it's necessary to fill in the
				gap between the shutter stops and the window. This creates a flat
				surface level with the stops, making it easy to slide the inserts in and
				out.
			</p>
			<p class="text-left">
				To create a flat surface for the insert to slide into, it's common to
				use small pieces of wood cut to the right size. If you're hesitant to
				make permanent changes, you can simply wedge these wood pieces in place
				without gluing or nailing them down. This works on the sides and bottom
				of the window. You don't need to fill the top.
			</p>
			<p class="text-left">
				The visual below shows shutters that are mounted inside the window frame
				so you can see the "gutter" or "channel" between the shutter stops and
				the window that would need to be filled:
			</p>
		</div>

		<Image src={shuttersDiagram} />
	</div>
</Section>
<GetAFreeEstimate />
